<script setup>
import { RichTextRenderer } from '@marvr/storyblok-rich-text-vue-renderer'

const props = defineProps({ blok: Object })
const activeIndex = ref(0)
function handleHover(index) {
  activeIndex.value = index
}

const containerClasses = computed(() => {
  switch (props.blok.container) {
    case 'full-width':
      return 'max-w-full'
    default:
      return 'mx-auto max-w-6xl'
  }
})

const textHoverColorClasses = computed(() => {
  switch (props.blok.color) {
    case 'yellow':
      return 'hover:!text-yellow-500'
    case 'blue':
      return 'hover:!text-blue-500'
    case 'green':
      return 'hover:!text-green-500'
    default:
      return 'hover:!text-secondary-red-100 '
  }
})

const classes = computed(() => `${containerClasses.value} ${props.blok.card ? '' : 'rounded-xl bg-white px-custom-sm py-custom-md shadow-custom-xl md:px-custom-lg md:py-custom-xl'} ${props.blok.overlap ? 'mt-custom-2xl' : '-mt-custom-2xl'}`)
</script>

<template>
  <div class="pt-mobile-navbar lg:pt-desktop-navbar">
    <div class="relative">
      <div v-if="blok?.breadcrumb && !blok?.media?.filename" class="pt-custom-lg">
        <LayoutsBreadcrump>
          <template #breadcrumbs>
            <div class="flex items-center gap-x-3 text-sm">
              <span class="flex md:hidden">
                <IconBreadcrump class="w-[9px] text-primary -scale-100" />
              </span>
              <NuxtLink
                class="hover:text-secondary-red-100"
                :to="localePath(
                  blok.link.cached_url && !blok.link.url ? `/${blok.link.cached_url}` : blok.link.url,
                )"
              >
                {{ blok.breadcrumb }}
              </NuxtLink>
              <span class="hidden md:flex">
                <IconBreadcrump class="w-[9px] text-primary" />
              </span>
            </div>
            <div class="hidden items-center gap-x-3 text-sm md:flex">
              <span class="capitalize">
                {{ blok.title }}
              </span>
            </div>
          </template>
        </LayoutsBreadcrump>
      </div>
      <div v-if="blok.media?.filename" class="relative pb-24 pt-12 lg:pb-40 lg:pt-32 md:pb-32 md:pt-28 sm:pb-28 sm:pt-20">
        <LayoutsBreadcrump v-if="blok?.breadcrumb">
          <template #breadcrumbs>
            <div class="flex items-center gap-x-3 text-sm text-white">
              <span class="flex sm:hidden">
                <IconBreadcrump class="w-[9px] text-white -scale-100" />
              </span>
              <NuxtLink
                :class="textHoverColorClasses"
                class="capitalize"
                :to="localePath({
                  path: rootedPath(blok.link.url ? blok.link.url : blok.link.cached_url),
                })"
              >
                {{ blok.breadcrumb }}
              </NuxtLink>
              <span class="hidden sm:flex">
                <IconBreadcrump class="w-[9px] text-white" />
              </span>
            </div>
            <div class="hidden items-center gap-x-3 text-sm text-white sm:flex">
              <span class="text-white">
                {{ blok.title }}
              </span>
            </div>
          </template>
        </LayoutsBreadcrump>
        <HeroBlock :image-url="`${blok.media?.filename}/m/1600x0/filters:quality(80)`" :bg-color="blok.color" :text-color="blok.color" :alt="blok.title">
          <template v-if="blok.header" #header>
            <span class="font-medium">{{ blok.header }}</span>
          </template>
          <template v-if="blok.title" #title>
            <span class="font-bold">{{ blok.title }}</span>
          </template>
        </HeroBlock>
      </div>
      <div v-else class="mt-3">
        <div class="container">
          <h1 class="mb-custom-xl text-3xl font-bold md:text-4xl">
            {{ blok.title }}
          </h1>
        </div>
      </div>
    </div>
    <div v-if="blok?.components?.length || blok?.content?.content?.length > 1" class="relative pb-mobile container lg:pb-desktop">
      <div v-if="blok?.components?.length" :class="blok?.components?.length && blok.card_option !== 'full-width' ? '3xl:translate-y-[-35%] lg:translate-y-[-25%] translate-y-[-7.5%]' : 'md:translate-y-[-5%] translate-y-[-2.5%]'">
        <div v-if="blok.card_option === 'full-width'" class="grid gap-y-2">
          <CardResourcesCard
            v-for="(item, index) in blok.components" :key="item._uid"
            class="[&(:last-child)]:mb-custom-lg"
            :content="item"
            :class="activeIndex === index ? 'shadow-custom-md z-20 scale-[102%]' : 'scale-100 z-0'"
            @mouseover="handleHover(index)"
          >
            <template #btn>
              <UIButton :theme="activeIndex === index ? '' : 'text'" size="sm" class="rounded-md">
                <span v-if="item.target">{{ $t('button.download') }}</span>
                <!-- <span v-else>{{ $t('button.explore') }}</span> -->
              </UIButton>
            </template>
          </CardResourcesCard>
        </div>
        <div v-else class="grid grid-cols-1 gap-x-custom-md gap-y-custom-lg lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2">
          <div
            v-for="item in blok.components"
            :key="item._uid"
            class="group relative flex items-center overflow-hidden rounded-md bg-white px-5 py-7 shadow-custom-xl transition-all duration-200 ease-in-out hover:bg-green-500"
          >
            <NuxtLink :to="localePath(item.link.cached_url && item.link.cached_url.startsWith('/') ? item.link.cached_url : `/${item.link.cached_url}`)">
              <h3 class="text-lg text-primary font-semibold capitalize group-hover:text-white">
                {{ item.title }}
              </h3>
            </NuxtLink>
            <div class="opacity-0 transition-all duration-200 ease-in-out group-hover:opacity-100">
              <DecorlationRstriped current-color="text-green-300" class="absolute bottom-[-15px] right-[-30px] h-[175px] w-[200px] origin-bottom-right" />
            </div>
          </div>
        </div>
      </div>
      <div :class="classes">
        <div v-if="blok" class="article" :class="blok.image">
          <RichTextRenderer :document="blok.content" />
        </div>
        <LayoutsContactFooter v-if="!blok.footer" />
      </div>
    </div>
  </div>
</template>
